import { Sigil } from "./observatory/Scene";
import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { track } from "@vercel/analytics";
import qr from "../assets/paymentQR.png";
import { Arrow } from "./Shared";
import { services, ritualGroups, servicePath, whatsapp } from "./catalog";
import { formatPrice } from "../utils/pricing";
import usePricing from "./PricingContext";
import { bookingPrice } from "./bookingPrice";
import { DiscountNote } from "./Shared";

export default function Payment() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const service = params.get("service") || "";
  const plan = params.get("plan") || "";
  const raw = params.get("amount");
  const { discountActive, discountMultiplier, loading } = usePricing();
  const price = bookingPrice(service, plan, discountActive, discountMultiplier);
  const amount = price?.amount ?? Number(raw);
  const format = params.get("callType");
  const legacyNames: Record<string, string> = {
    "Panchamukhi Healing": "hanuman-healing",
    "Therapy & Counselling": "therapy",
  };
  const matched = services.find(
    (s) =>
      s.title === service ||
      s.paymentName === service ||
      s.slug === legacyNames[service],
  );
  const ritual = ritualGroups.some((r) => r.title === service);
  const valid = Boolean(
    (matched || ritual) && price &&
    plan &&
    raw !== null &&
    /^\d+(\.\d{1,2})?$/.test(raw) &&
    Number.isFinite(amount) &&
    amount >= 0 &&
    amount <= 1000000,
  );
  const [step,setStep]=useState(0);
  const stepHeading=useRef<HTMLHeadingElement>(null);
  function changeStep(next:number){setStep(next);requestAnimationFrame(()=>{stepHeading.current?.focus();stepHeading.current?.scrollIntoView({block:'center',behavior:document.documentElement.dataset.motion==='paused'?'auto':'smooth'});});}
  const [status, setStatus] = useState("");
  const upiId = "radiansoul03@okicici";
  const upi = `upi://pay?${new URLSearchParams({ pa: upiId, pn: "RadianSoul", am: String(amount), cu: "INR", tn: `${service} - ${plan}` })}`;
  async function copyUPI() {
    try {
      await navigator.clipboard.writeText(upiId);
      setStatus("UPI ID copied.");
    } catch {
      setStatus(`Copy this UPI ID manually: ${upiId}`);
    }
  }
  if (!valid)
    return (
      <section className="container empty-state payment-empty">
        <p className="eyebrow">LET’S START WITH A SESSION</p>
        <h1>
          Your booking
          <br />
          starts here.
        </h1>
        <p>
          Choose a service and a plan so we can show the correct booking
          details.
        </p>
        <Link to="/services" className="button">
          Explore services <Arrow />
        </Link>
      </section>
    );
  const back = matched ? servicePath(matched) : "/services/tarot-spells";
  const returnTo = typeof location.state?.returnTo === "string" && location.state.returnTo.startsWith(`${back}?`) ? location.state.returnTo : `${back}#plans`;
  return (
    <section className="container payment-page observatory-payment">
      <Link to={returnTo} className="text-link">
        ← Back to session options
      </Link>
      <div className="payment-heading">
        <p className="eyebrow">YOUR BOOKING</p>
        <h1>
          {amount === 0 ? "Let’s arrange the details." : "A moment for you."}
        </h1>
        <p>
          {amount === 0
            ? "Speak with Vaanii to arrange this session. No payment is requested here."
            : "Review your selection, make your payment, then confirm your session on WhatsApp."}
        </p>
      </div>
      <nav className="booking-journey" aria-label="Booking steps">{[{title:'Your choice',note:'A moment to review',value:0},...(amount>0?[{title:'Your payment',note:'Directly through UPI',value:1}]:[]),{title:'Your beginning',note:'Arrange a time together',value:2}].map((item,i)=><button key={item.value} disabled={loading && item.value > 0} aria-current={step===item.value?'step':undefined} onClick={()=>changeStep(item.value)}><span>0{i+1}</span><strong>{item.title}</strong><small>{item.note}</small></button>)}</nav>
      <div className="payment-grid">
        <aside id="booking-review" className="order-summary">
          <div className="booking-seal"><Sigil slug={matched?.slug||"tarot-spells"}/></div><p className="eyebrow">YOUR INVITATION</p>
          <h2>{service}</h2>
          <dl>
            <div>
              <dt>Your plan</dt>
              <dd>{plan}</dd>
            </div>
            {format && (
              <div>
                <dt>Format</dt>
                <dd>{format === "Audio" ? "Audio call" : format}</dd>
              </div>
            )}
            <div className="order-total">
              <dt>{amount === 0 ? "Payment" : "Total to pay"}</dt>
              <dd>
                {discountActive && price && <del className="payment-original">{price.originalText}</del>}
                {loading ? "Checking price…" : amount === 0 ? "Arrange with Vaanii" : formatPrice(amount)}
              </dd>
            </div>
          </dl>
          <DiscountNote />
          {!loading && raw !== null && Number(raw) !== amount && <p className="booking-note">Your total has been updated to the current session price shown above.</p>}
          <p className="booking-note">
            Your time slot is arranged separately. If you haven’t checked
            availability, you can do that before paying.
          </p>
          <a
            className="text-link"
            href={whatsapp(
              `Hello, I would like to check availability for ${service} — ${plan}${format ? ` (${format})` : ""}.`,
            )}
            target="_blank"
            rel="noreferrer"
          >
            Check availability <Arrow diagonal />
          </a>
        </aside>
        <div className="payment-methods" key={step}>
          <h2 ref={stepHeading} tabIndex={-1} className="payment-step-heading">{step===0?'A space, just for you.':step===1?'One step closer.':'Let’s find your moment.'}</h2>
          {step===0&&<div className="payment-welcome"><span className="handwritten">A beginning, made personal.</span><p>Take a moment to check your session and format. Your time is arranged directly with Vaanii, so you can ask about availability before paying.</p><ol><li>Review your selection alongside.</li><li>{amount>0?'Complete payment with your preferred UPI app.':'No payment is requested for this selection.'}</li><li>Connect on WhatsApp to arrange your time.</li></ol><button className="portal-button" disabled={loading} onClick={()=>changeStep(amount>0?1:2)}><span>{amount>0?'Continue to payment':'Arrange my session'}</span><Arrow/></button></div>}
          {amount > 0 && !loading && step===1 && (
            <>
              <div className="numbered-heading">
                <span>01</span>
                <h2>Pay with UPI</h2>
              </div>
              <div id="booking-payment" className="upi-panel">
                <img src={qr} alt="Radian Soul UPI payment QR code" />
                <div>
                  <p>Scan with your UPI app</p>
                  <small>
                    Enter {formatPrice(amount)} and check the recipient before
                    confirming.
                  </small>
                  <button className="copy-upi" onClick={copyUPI}>
                    {upiId} <span>{status === "UPI ID copied." ? "Copied ✓" : "Copy ↗"}</span>
                  </button>
                  <a
                    href={upi}
                    className="button"
                    onClick={(event) => {
                      if (
                        !/android|iPhone|iPad|iPod/i.test(navigator.userAgent)
                      ) {
                        event.preventDefault();
                        setStatus(
                          "On a computer, scan the QR with your phone. On mobile, open this page with a UPI app installed.",
                        );
                      } else {
                        track("UPI payment opened", { service });
                        setStatus(
                          "After paying in your UPI app, return here to confirm on WhatsApp.",
                        );
                      }
                    }}
                  >
                    Open UPI app <Arrow diagonal />
                  </a>
                  <p className="payment-status" role="status">
                    {status}
                  </p>
                </div>
              </div>
              <button className="portal-button payment-next" onClick={()=>changeStep(2)}><span>Payment sent? Next step</span><Arrow/></button><p className="booking-footnote">Only continue after completing payment in your UPI app. This website cannot verify it automatically.</p>
            </>
          )}
          {step===2&&<div id="booking-confirm" className="confirmation-step">
            <div className="numbered-heading">
              <span>{amount > 0 ? "02" : "01"}</span>
              <h2>
                {amount > 0 ? "Confirm your session" : "Arrange your session"}
              </h2>
            </div>
            <p>
              {amount > 0
                ? "After payment, share your receipt on WhatsApp. We’ll verify it and arrange your session time with you."
                : "Send your selection to Vaanii to discuss the next step."}
            </p>
            <a
              className="button"
              href={whatsapp(
                `Hello, ${amount > 0 ? "I would like to confirm my payment of " + formatPrice(amount) + " for" : "I would like to arrange"} ${service} — ${plan}${format ? ` (${format})` : ""}. Please share available slots.`,
              )}
              target="_blank"
              rel="noreferrer"
              onClick={() => track("Booking confirmation opened", { service })}
            >
              {amount > 0
                ? "I’ve paid · Confirm on WhatsApp"
                : "Arrange on WhatsApp"}{" "}
              <Arrow diagonal />
            </a>
            <p className="booking-footnote">
              This page does not verify payment automatically.
            </p>
          </div>
          }
          {step>0&&<button className="text-link payment-back" onClick={()=>changeStep(step===2&&amount>0?1:0)}>← {step===1?"Review my selection":"Previous step"}</button>}
          <div className="payment-help">
            <h3>Need help with payment?</h3>
            <a
              className="text-link"
              href={whatsapp(
                `Hello, please help me with payment for ${service} — ${plan}.`,
              )}
              target="_blank"
              rel="noreferrer"
            >
              Request a payment QR on WhatsApp <Arrow diagonal />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
